// Import necessary modules and hooks
import PropTypes from "prop-types";
import { useState, forwardRef, useRef, useImperativeHandle, useCallback, useEffect } from "react";
import { drawConnectors, drawLandmarks, lerp } from "@mediapipe/drawing_utils";
import { saveAs } from 'file-saver';
import {
  //FACEMESH_TESSELATION,
  POSE_CONNECTIONS,
  HAND_CONNECTIONS,
  //FACEMESH_FACE_OVAL,
  //FACEMESH_CONTOURS,
} from "@mediapipe/holistic";

// Material UI components and icons
import { Modal, Divider, Slide, LinearProgress, MenuItem, Menu, Tooltip } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SwitchVideoIcon from "@mui/icons-material/SwitchVideo";
import CameraswitchIcon from "@mui/icons-material/Cameraswitch";
import RadioButtonCheckedIcon from "@mui/icons-material/RadioButtonChecked";
import StopIcon from "@mui/icons-material/Stop";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import SlowMotionVideoIcon from "@mui/icons-material/SlowMotionVideo";

// Custom components and hooks
import MKBox from "components/MKBox";
import MKButton from "components/MKButton";
import MKTypography from "components/MKTypography";
import Webcam from "react-webcam";
import { useHolistic } from "../hooks/useMediapipe"; // Ensure this is the modified hook
import { usePersistentConfig } from "hooks/usePersistentConfig";
import { RECORDING } from "constants";
import { leftHandTemplate, rightHandTemplate, faceTemplate } from "utils/landmark-helper";
import InstructionsModal from "./InstructionsModal";

const RecordSignModal = forwardRef(({ onRecorded }, ref) => {
  const { showRecordingPlayStopButtons, storedDeviceId, setStoredDeviceId } = usePersistentConfig();

  const [selectCameraEl, setSelectCameraEl] = useState(null);
  const [mirrorCamera, setMirrorCamera] = useState(true);
  const [deviceId, setDeviceId] = useState(storedDeviceId);
  const [devices, setDevices] = useState([]);
  const [show, setShow] = useState(false);
  const toggleModal = () => setShow(!show);
  const [isUploadProcessing, setIsUploadProcessing] = useState(false);

  const instructionsModalRef = useRef(null);

  const {
    webcamRef,

    isInitializingHolistic,
    initHolistic,
    holisticRef,

    isInitializingCamera,
    initCamera,
    cameraRef,
    reset: mediaPipeReset,
    recordFrame: isRecording,

    startRecording: startRecordingHolistic,
    stopRecording: stopRecordingHolistic,

    progressHolisticFrame,
    processingHolisticFrame,

    processUploadedVideo, // Add this line to get processUploadedVideo function
  } = useHolistic();

  useImperativeHandle(ref, () => ({
    showModal(visible = true) {
      setShow(visible);
    },
  }));

  // #region Camera UI
  const handleDevices = useCallback(
    (mediaDevices) =>
      setDevices(mediaDevices.filter(({ kind, deviceId }) => kind === "videoinput" && deviceId)),
    [setDevices]
  );

  const showCameraList = (event) => {
    setSelectCameraEl(event.currentTarget);
  };

  const closeCameraList = (selectedDeviceId) => {
    if (selectedDeviceId) {
      setDeviceId(selectedDeviceId);
      setStoredDeviceId(selectedDeviceId);
    }

    setSelectCameraEl(null);
  };

  useEffect(() => {
    navigator.mediaDevices.enumerateDevices().then(handleDevices);
  }, [handleDevices]);

  const onWebcamUserMedia = async () => {
    clearCanvas();
    await mediaPipeReset();
    await initHolistic(onHolisticResults);
    await initCamera(deviceId);
  };

  // #endregion Camera UI

  // #region Paint UI
  const clearCanvas = () => {
    const canvas = document.getElementById("canvas");
    const canvasCtx = canvas.getContext("2d");
    canvasCtx.clearRect(0, 0, canvas.width, canvas.height);
  };

  const paintTemplateLandmarks = (results) => {
    if (isRecording.current) return;

    const canvas = document.getElementById("canvas");
    const canvasCtx = canvas.getContext("2d");

    canvasCtx.save();
    canvasCtx.globalCompositeOperation = "source-over";

    // Draw templates if not in start position
    // if (!results.inStartPosition.face) {
    //   drawConnectors(canvasCtx, faceTemplate, FACEMESH_CONTOURS, {
    //     color: "#ffffff80",
    //     lineWidth: 1,
    //   });
    // }

    if (!results.inStartPosition.leftHand) {
      drawConnectors(canvasCtx, leftHandTemplate, HAND_CONNECTIONS, {
        color: "#ffffff80",
        lineWidth: 2,
      });
      drawLandmarks(canvasCtx, leftHandTemplate, {
        radius: (data) => lerp(data.from.z, -0.15, 0.1, 5, 1),
        color: "#ffffff40",
        fillColor: "transparent",
        lineWidth: 1,
      });
    }

    if (!results.inStartPosition.rightHand) {
      drawConnectors(canvasCtx, rightHandTemplate, HAND_CONNECTIONS, {
        color: "#ffffff80",
        lineWidth: 2,
      });
      drawLandmarks(canvasCtx, rightHandTemplate, {
        radius: (data) => lerp(data.from.z, -0.15, 0.1, 5, 1),
        color: "#ffffff40",
        fillColor: "transparent",
        lineWidth: 1,
      });
    }
    canvasCtx.restore();
  };

  const paintLandmarks = (results) => {
    if (isRecording.current) return;

    const canvas = document.getElementById("canvas");
    const canvasCtx = canvas.getContext("2d");

    canvasCtx.save();
    canvasCtx.globalCompositeOperation = "source-over";

    // Draw landmarks with colors based on inStartPosition
    // const faceColor = results.inStartPosition.face ? "#00CC0070" : "#CC000070";
    // drawConnectors(canvasCtx, results.faceLandmarks, FACEMESH_FACE_OVAL, {
    //   color: faceColor,
    //   lineWidth: 1,
    // });

    const leftHandColor = results.inStartPosition.leftHand ? "#00CC0070" : "#CC000070";
    drawConnectors(canvasCtx, results.leftHandLandmarks, HAND_CONNECTIONS, {
      color: leftHandColor,
      lineWidth: 2,
    });
    drawLandmarks(canvasCtx, results.leftHandLandmarks, {
      radius: (data) => lerp(data.from.z, -0.15, 0.1, 5, 1),
      fillColor: "transparent",
      lineWidth: 1,
    });

    const rightHandColor = results.inStartPosition.rightHand ? "#00CC0070" : "#CC000070";
    drawConnectors(canvasCtx, results.rightHandLandmarks, HAND_CONNECTIONS, {
      color: rightHandColor,
      lineWidth: 2,
    });
    drawLandmarks(canvasCtx, results.rightHandLandmarks, {
      radius: (data) => lerp(data.from.z, -0.15, 0.1, 5, 1),
      fillColor: "transparent",
      lineWidth: 1,
    });

    canvasCtx.restore();
  };

  // #endregion Paint UI

  // #region Recording

  const [processingFrames, setProcessingFrames] = useState(false);

  const startRecording = () => {
    startRecordingHolistic();
    clearCanvas();
  };

  const endRecordingAsync = async () => {
    const data = await stopRecordingHolistic();
    if (onRecorded && !isUploadProcessing) {  // Ensure it's not called if the upload is in progress
      onRecorded(data);
    }
    setShow(false);
    setProcessingFrames(false);
  };
  
  const endRecording = () => {
    setProcessingFrames(true);
  };

  useEffect(() => {
    if (processingFrames) {
      // Timeout to update UI before processing
      setTimeout(endRecordingAsync, 10);
    }
  }, [processingFrames]);

  // #endregion Recording

  // #region Countdown

  const [countdownLabel, setCountdownLabel] = useState(RECORDING.PREPARATION_TIME);
  const [showCountdown, setShowCountdown] = useState(false);
  const countdown = useRef(RECORDING.PREPARATION_TIME);
  const inCountdown = useRef(false);
  const timer = useRef(null);

  const startCountdown = () => {
    inCountdown.current = true;
    countdown.current = RECORDING.PREPARATION_TIME;
    setCountdownLabel(countdown.current);
    setShowCountdown(true);

    timer.current = setTimeout(() => countdownStep(), 1000);
  };

  const countdownStep = () => {
    if (countdown.current === 0) {
      startRecording();
      setShowCountdown(false);
      timer.current = setTimeout(() => endRecording(), RECORDING.RECORDING_TIME);
      inCountdown.current = false;
    } else {
      countdown.current -= 1;
      setCountdownLabel(countdown.current);
      timer.current = setTimeout(() => countdownStep(), 1000);
    }
  };

  const resetCountdown = () => {
    if (timer.current) clearTimeout(timer.current);
    inCountdown.current = false;
    setShowCountdown(false);
  };

  // #endregion Countdown

  // #region Video Upload Handling

  // Function to handle video upload
  const handleVideoUpload = async (event) => {

    // Load the meaning.json file from the public folder
    let meanings = {};
    try {
      const response = await fetch('/meaning.json'); // Public folder files are accessed at the root
      if (response.ok) {
        meanings = await response.json();
      } else {
        console.error('Failed to load meaning.json');
        return;
      }
    } catch (error) {
      console.error('Error fetching meaning.json:', error);
      return;
    }

    const files = Array.from(event.target.files); 
    const processingTimes_mp = [];
    const processingTimes_inf = [];
    const outputs = [];

    for (const file of files) {
      if (!processingFrames) { 
        
        //clearCanvas();
        await mediaPipeReset();
        await initHolistic(onHolisticResultsForUpload);
        setProcessingFrames(true);
        setIsUploadProcessing(true);
  
        const startTime = performance.now(); // Start timing
  
        const data = await processUploadedVideo(file);
        
        const endtime_mp = performance.now();
        const duration_mp = endtime_mp - startTime
        const fileName = file.name;
        if (onRecorded) await onRecorded(data, meanings, fileName, duration_mp);

        // Wait until data.signs length is more than 0
        while (data.signs.length === 0) {
          await new Promise((resolve) => setTimeout(resolve, 50));
        }

        const endTime_inf = performance.now(); // End timing
        const duration_inf = endTime_inf - endtime_mp
        processingTimes_mp.push(duration_mp);
        processingTimes_inf.push(duration_inf);
        //console.log(`Processing time for ${file.name}: ${duration} ms`);

        console.log('Processed:', fileName);
        
        // Map predictions using meanings
        //const predictions = data.signs.map((pred) => meanings[pred.toString()] || `Unknown (${pred})`);
        //console.log('Top 5 Predictions (Mapped):', predictions);
        const predictions = data.signs;

        outputs.push({ fileName, predictions, duration_mp, duration_inf });
  
        setProcessingFrames(false);
        setIsUploadProcessing(false);
      }
    }
    // Calculate mean and standard deviation
    //const mean_mp = processingTimes_mp.reduce((acc, time) => acc + time, 0) / processingTimes_mp.length;
    //const variance_mp = processingTimes_mp.reduce((acc, time) => acc + Math.pow(time - mean_mp, 2), 0) / processingTimes_mp.length;
    //const standardDeviation_mp = Math.sqrt(variance_mp);

    //const mean_inf = processingTimes_inf.reduce((acc, time) => acc + time, 0) / processingTimes_inf.length;
    //const variance_inf = processingTimes_inf.reduce((acc, time) => acc + Math.pow(time - mean_inf, 2), 0) / processingTimes_inf.length;
    //const standardDeviation_inf = Math.sqrt(variance_inf);

    // const txtContent = `
    //   MediaPipe Processing Times Statistics:
    //   --------------------------------------
    //   Mean: ${mean_mp.toFixed(2)} ms
    //   Variance: ${variance_mp.toFixed(2)} ms²
    //   Standard Deviation: ${standardDeviation_mp.toFixed(2)} ms

    //   Inference Processing Times Statistics:
    //   ---------------------------------------
    //   Mean: ${mean_inf.toFixed(2)} ms
    //   Variance: ${variance_inf.toFixed(2)} ms²
    //   Standard Deviation: ${standardDeviation_inf.toFixed(2)} ms
    // `;

    // Create a blob and save it as a TXT file
    //const blob_txt = new Blob([txtContent], { type: 'text/plain;charset=utf-8;' });
    //saveAs(blob_txt, 'processing_statistics.txt');

    console.log('Top 5 Predictions:', outputs);

    // console.log(`Mean processing time: ${mean.toFixed(2)} ms`);
    // console.log(`Standard deviation of processing time: ${standardDeviation.toFixed(2)} ms`);
    // console.log('Processed videos:', processingTimes.length);

    // Generate CSV content
    // const csvContent = 'video,Predictions,Duration_mp,Duration_inf\n' + 
    //   outputs.map(({ fileName, predictions, duration_mp, duration_inf }) => 
    //     `${fileName},"${predictions.join(', ')}",${duration_mp},${duration_inf}`
    //   ).join('\n');

    // Save the CSV file
    //const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    //saveAs(blob, 'predictions.csv');
  };

  // Separate onHolisticResults function for uploaded video processing
  const onHolisticResultsForUpload = (results) => {
    // Handle the results from the uploaded video frames
    // You can process the results as needed or update the UI
  };

  // #endregion Video Upload Handling

  const onHolisticResults = (results) => {
    clearCanvas();

    paintTemplateLandmarks(results);
    paintLandmarks(results);

    if (results.inStartPosition.all && !isRecording.current && !inCountdown.current) {
      startCountdown();
    }
  };

  useEffect(() => {
    return () => {
      mediaPipeReset();
      resetCountdown();
    };
  }, [show]);

  return (
    <>
      <InstructionsModal ref={instructionsModalRef}></InstructionsModal>

      <Modal open={show} onClose={toggleModal} sx={{ display: "grid", placeItems: "center" }}>
        <Slide
          direction="down"
          in={show}
          timeout={500}
          sx={{ width: { xs: "auto", md: 500 }, margin: { xs: 1, md: 0 } }}
        >
          <MKBox
            position="relative"
            width="500px"
            display="flex"
            flexDirection="column"
            borderRadius="xl"
            bgColor="white"
            shadow="xl"
          >
            <MKBox display="flex" alignItems="center" justifyContent="space-between" p={2}>
              <MKTypography variant="h5">Grabar seña</MKTypography>
              <MKBox display="flex" alignItems="center" justifyContent="space-between">
                {inCountdown.current && (
                  <RadioButtonCheckedIcon
                    fontSize="medium"
                    className="countdown-pulse"
                    sx={{ mr: 3 }}
                  />
                )}

                {isRecording.current && (
                  <RadioButtonCheckedIcon
                    fontSize="medium"
                    className="recording-pulse"
                    sx={{ mr: 3, color: "#e7464c" }}
                  />
                )}

                {showRecordingPlayStopButtons && (
                  <Tooltip title="Iniciar grabación">
                    <SlowMotionVideoIcon
                      fontSize="medium"
                      sx={{ cursor: "pointer", mr: 3 }}
                      onClick={() => startCountdown()}
                    />
                  </Tooltip>
                )}

                {showRecordingPlayStopButtons && (
                  <PlayArrowIcon
                    fontSize="medium"
                    sx={{ cursor: "pointer", mr: 3 }}
                    onClick={() => startRecording()}
                  />
                )}
                {showRecordingPlayStopButtons && (
                  <StopIcon
                    fontSize="medium"
                    sx={{ cursor: "pointer", mr: 3 }}
                    onClick={() => endRecording()}
                  />
                )}
                {showRecordingPlayStopButtons && (
                  <Tooltip title="Invertir cámara">
                    <SwitchVideoIcon
                      fontSize="medium"
                      sx={{ cursor: "pointer", mr: 3 }}
                      onClick={() => setMirrorCamera(!mirrorCamera)}
                    />
                  </Tooltip>
                )}
                {showRecordingPlayStopButtons && devices && devices.length > 0 && (
                  <>
                    <Tooltip title="Cambiar cámara">
                      <CameraswitchIcon
                        fontSize="medium"
                        sx={{ cursor: "pointer", mr: 3 }}
                        onClick={showCameraList}
                      />
                    </Tooltip>
                    <Menu
                      id="lock-menu"
                      anchorEl={selectCameraEl}
                      open={selectCameraEl != null}
                      onClose={() => closeCameraList()}
                      MenuListProps={{
                        "aria-labelledby": "lock-button",
                        role: "listbox",
                      }}
                    >
                      {devices.map((device, index) => (
                        <MenuItem
                          onClick={() => closeCameraList(device.deviceId)}
                          key={device.deviceId}
                        >
                          {device.label}
                        </MenuItem>
                      ))}
                    </Menu>
                  </>
                )}
                <CloseIcon fontSize="medium" sx={{ cursor: "pointer" }} onClick={toggleModal} />
              </MKBox>
            </MKBox>
            <Divider sx={{ my: 0 }} />
            <MKBox p={2}>
              <div style={{ position: "relative" }}>
                <Webcam
                  ref={webcamRef}
                  audio={false}
                  videoConstraints={{ deviceId: deviceId }}
                  mirrored={mirrorCamera}
                  width={"100%"}
                  style={{ borderRadius: 8, marginBottom: -10, width: "100%" }}
                  onUserMedia={(stream) => onWebcamUserMedia(stream)}
                />
                <canvas
                  id="canvas"
                  style={{
                    position: "absolute",
                    left: 0,
                    width: "100%",
                    top: 0,
                    height: "100%",
                    transform: mirrorCamera ? "scaleX(-1)" : "none",
                  }}
                ></canvas>

                {processingFrames && (
                  <div
                    style={{
                      position: "absolute",
                      left: 0,
                      width: "100%",
                      top: 0,
                      height: "100%",
                      display: "flex",
                      alignContent: "center",
                      justifyContent: "center",
                      flexWrap: "wrap",
                    }}
                  >
                    <div
                      style={{
                        position: "absolute",
                        left: 0,
                        width: "100%",
                        top: 0,
                        height: "100%",
                        backgroundColor: "black",
                        opacity: 0.7,
                        borderRadius: 8,
                      }}
                    ></div>
                    <div style={{ zIndex: 100, textAlign: "center" }}>
                      <div style={{ color: "#ffffffaa" }}>Procesando...</div>
                      <MKTypography variant="h1" color="white" style={{ fontSize: 70 }}>
                        {Math.floor(progressHolisticFrame)}%
                      </MKTypography>
                    </div>
                  </div>
                )}
                {showCountdown && (
                  <div
                    style={{
                      position: "absolute",
                      left: 0,
                      width: "100%",
                      top: 0,
                      height: "100%",
                      display: "flex",
                      alignContent: "center",
                      justifyContent: "center",
                      flexWrap: "wrap",
                    }}
                  >
                    <div
                      style={{
                        position: "absolute",
                        left: 0,
                        width: "100%",
                        top: 0,
                        height: "100%",
                        backgroundColor: "black",
                        opacity: 0.7,
                        borderRadius: 8,
                      }}
                    ></div>
                    <div style={{ zIndex: 100, textAlign: "center" }}>
                      <div style={{ color: "#ffffffaa" }}>Grabando en</div>
                      <MKTypography variant="h1" color="white" style={{ fontSize: 70 }}>
                        {countdownLabel}
                      </MKTypography>
                    </div>
                  </div>
                )}
              </div>
            </MKBox>

            <Divider sx={{ my: 0 }} />
            {!show ? (
              <LinearProgress
                variant="indeterminate"
                value={true}
                sx={{ width: "100%", overflow: "hidden", height: 4 }}
              ></LinearProgress>
            ) : (
              <MKBox sx={{ width: "100%", overflow: "hidden", height: 4 }}></MKBox>
            )}
            <MKBox display="flex" justifyContent="space-between" p={1.5}>
              <MKButton
                variant="gradient"
                color="info"
                onClick={() => instructionsModalRef.current.showModal(true, true)}
              >
                Instrucciones
              </MKButton>

              <MKButton variant="gradient" color="info" component="label">
                Subir Videos
                <input
                  type="file"
                  accept="video/*"
                  hidden
                  multiple  
                  onChange={handleVideoUpload}
                />
              </MKButton>

              <MKButton variant="gradient" color="info" onClick={toggleModal}>
                Cerrar
              </MKButton>
            </MKBox>
          </MKBox>
        </Slide>
      </Modal>
    </>
  );
});

RecordSignModal.propTypes = {
  onRecorded: PropTypes.func,
};

export default RecordSignModal;
